import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import ScriptLoader from 'yoda-core-components/lib/helpers/ScriptLoader/ScriptLoader';

const DEFAULT_SCRIPT_URL = 'https://cnstrc.com/js/cust/catalyst_brands_jcp_YG2waW.js';
const DEFAULT_AB_COOKIE_NAME = 'CRtest';

const readCookie = (name) => {
    if (__SERVER__ || typeof document === 'undefined') return '';
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
};

/**
 * Default exclude paths for Constructor beacon script.
 * Can be overridden via preferences.constructorIO.common.excludePaths
 */
const DEFAULT_EXCLUDE_PATHS = [
    '/checkout/shipping',
    '/checkout/payment',
    '/checkout/review',
    '/checkout/signin',
    '/cart/signin',
    '/account',
];

const shouldLoadConstructorScript = (preferences) => {
    if (typeof window === 'undefined') return false;
    const path = ((window.location && window.location.pathname) || '').toLowerCase();
    const excludePaths =
        (preferences &&
            preferences.constructorIO &&
            preferences.constructorIO.common &&
            preferences.constructorIO.common.excludePaths) ||
        DEFAULT_EXCLUDE_PATHS;
    return !excludePaths.some((p) => path.startsWith(p));
};

/**
 * Constructor.io Global Config Initializer
 * Sets window.cnstrc with userId for behavioral tracking.
 * Loads Constructor.io client script via ScriptLoader.
 */
const initConstructorIO = (indexKey, scriptUrl, preferences) => {
    if (__SERVER__) return;
    if (window.cnstrc && window.cnstrc.indexKey) return;
    if (!shouldLoadConstructorScript(preferences)) return;

    const userState = LocalStorage.getData('DP_USER_STATE');
    const isLoggedIn = userState && userState !== '0';

    window.cnstrc = window.cnstrc || {};
    window.cnstrc.indexKey = indexKey;

    if (isLoggedIn) {
        window.cnstrc.userId = LocalStorage.getData('ACCOUNT_ID');
    }

    const src = scriptUrl || DEFAULT_SCRIPT_URL;
    const enableBeaconConstructorScript =
        preferences?.constructorIO?.common?.enableBeaconConstructorScript !== false;
    const existingScript = document.getElementById('cnstrc-client');
    if (enableBeaconConstructorScript && !existingScript) {
        ScriptLoader.load({ src, async: false, defer: true });
        const scripts = document.body.getElementsByTagName('script');
        const lastScript = scripts[scripts.length - 1];
        if (lastScript) lastScript.id = 'cnstrc-client';
    }
};

/**
 * Updates Constructor.io userId on login/logout.
 */
export const updateConstructorIOUserId = (clear) => {
    if (__SERVER__ || !window.cnstrc) return;
    if (clear) {
        delete window.cnstrc.userId;
        return;
    }
    const userState = LocalStorage.getData('DP_USER_STATE');
    if (userState && userState !== '0') {
        window.cnstrc.userId = LocalStorage.getData('ACCOUNT_ID');
    }
};

/**
 * Returns the AB test cookie name from preferences or default.
 */
export const getConstructorABCookieName = (preferences) => {
    return preferences?.constructorIO?.common?.cookieName || DEFAULT_AB_COOKIE_NAME;
};

/**
 * Determines if Constructor.io should be used as the search provider.
 * Reads CRtest cookie internally for AB testing check.
 *
 * @param {object} featureFlags - featureFlags object containing enableConstructor and enableConstructorABTesting
 * @param {object} preferences - preferences object to get cookie name from
 * @returns {boolean}
 */
export const isConstructorEnabled = (featureFlags = {}, preferences) => {
    const constructorFlag = featureFlags?.enableConstructor || false;
    const abTesting = featureFlags?.enableConstructorABTesting || false;

    if (constructorFlag) return true;
    if (abTesting) {
        const cookieName = getConstructorABCookieName(preferences);
        const crTestCookieValue = readCookie(cookieName);
        if (crTestCookieValue) return true;
    }
    return false;
};

export default initConstructorIO;
